import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LuBox,
  LuFileText,
  LuTriangleAlert,
  LuDownload,
  LuPlus,
  LuSearch,
  LuImage,
  LuEllipsis,
  LuTrash2,
  LuX,
  LuRefreshCw,
} from "react-icons/lu";
import {
  getProductsWithOrganization,
  getIncompleteProducts,
  deleteProductsBatch,
} from "../../../../api/products";
import {
  requestTableRefresh,
  updateFloatPanel,
} from "../../../../reducers/userConfigSlice";
import { exportCollection } from "../../../../components/collectionTools";
import { ProductMonitoringFields } from "../../../../components/productMonitoring";
import { ProductStatusToggle } from "./productStatusToggle";
import { Edition } from "./edition";
import {
  productView,
  productPriceWithTax,
  productMoney,
} from "./productListModel";
import "./productList.css";
import {ProductSort} from "./productSort";

export const ProductList = () => {
  const dispatch = useDispatch();
  const categories =
    useSelector((state) => state.aplicationConfig.corporationData.categories) ||
    [];
  const catalogs =
    useSelector((state) => state.aplicationConfig.corporationData.catalogs) ||
    [];
  const { visible: editorVisible } = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel,
  );
  const refreshVersion = useSelector(
    (state) => state.aplicationConfig.userConfig.datatable.refreshVersion || 0,
  );
  const [read, setRead] = useState({
    pending: true,
    error: false,
    reviewError: false,
    rows: [],
  });
  const [retry, setRetry] = useState(0);
  const [view, setView] = useState({
    tab: "all",
    search: "",
    status: "",
    catalog: "",
    category: "",
    sort: "name-asc",
  });
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [menu, setMenu] = useState(null);
  const [mutationBusy, setMutationBusy] = useState(false);
  const [deletion, setDeletion] = useState(null);
  const allBox = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    if (editorVisible) return;
    let active = true;
    setRead((previous) => ({ ...previous, pending: true, error: false }));
    Promise.allSettled([
      getProductsWithOrganization(),
      getIncompleteProducts(),
    ]).then(([all, review]) => {
      if (!active) return;
      if (
        all.status !== "fulfilled" ||
        all.value?.error !== false ||
        !Array.isArray(all.value?.data)
      ) {
        setRead((previous) => ({ ...previous, pending: false, error: true }));
        return;
      }
      const reviewFailed =
        review.status !== "fulfilled" ||
        review.value?.error !== false ||
        !Array.isArray(review.value?.data);
      const missing = new Map(
        (reviewFailed ? [] : review.value.data).map((row) => [
          row.productId,
          row.missingFields || [],
        ]),
      );
      setRead({
        pending: false,
        error: false,
        reviewError: reviewFailed,
        rows: all.value.data.map((row) => ({
          ...row,
          missingFields: missing.get(row.productId) || [],
        })),
      });
      setSelected([]);
    });
    return () => {
      active = false;
    };
  }, [editorVisible, refreshVersion, retry]);
  useEffect(() => {
    setSelected([]);
    setPage(1);
    setMenu(null);
  }, [view]);
  useEffect(() => {
    if (!menu) return;
    const closeOutside = (event) => {
      if (mutationBusy) return;
      if (
        !menuRef.current?.contains(event.target) &&
        !event.target.closest?.(".productRowTrigger")
      )
        setMenu(null);
    };
    const closeEscape = (event) => {
      if (event.key === "Escape" && !mutationBusy) {
        setMenu(null);
        document.getElementById(`product-actions-${menu.id}`)?.focus();
      }
    };
    const closeOnScroll = (event) => {
      if (mutationBusy) return;
      if (!menuRef.current?.contains(event.target)) setMenu(null);
    };
    document.addEventListener("scroll", closeOnScroll, true);
    const closeOnResize = () => {
      if (!mutationBusy) setMenu(null);
    };
    window.addEventListener("resize", closeOnResize);
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    return () => {
      document.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", closeOnResize);
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeEscape);
    };
  }, [menu, mutationBusy]);
  const rows = useMemo(() => productView(read.rows, view), [read.rows, view]);
  const pages = Math.max(1, Math.ceil(rows.length / 20));
  const currentPage = Math.min(page, pages);
  const pageRows = rows.slice((currentPage - 1) * 20, currentPage * 20);
  const allSelected =
    pageRows.length > 0 &&
    pageRows.every((row) => selected.includes(row.productId));
  useEffect(() => {
    if (allBox.current)
      allBox.current.indeterminate =
        !allSelected &&
        pageRows.some((row) => selected.includes(row.productId));
  }, [allSelected, pageRows, selected]);
  const changeView = (key, value) =>
    setView((previous) => ({ ...previous, [key]: value }));
  const clear = () =>
    setView({
      tab: "all",
      search: "",
      status: "",
      catalog: "",
      category: "",
      sort: "name-asc",
    });
  const open = (row) => {
    if (mutationBusy) return;
    setMenu(null);
    dispatch(
      updateFloatPanel({
        visible: true,
        queryId: row?.productId || null,
        isEmpty: !row,
        data: row || null,
      }),
    );
  };
  const toggle = (id) =>
    setSelected((previous) =>
      previous.includes(id)
        ? previous.filter((value) => value !== id)
        : [...previous, id],
    );
  const relation = (ids, items, key) =>
    ids?.length
      ? ids
          .map(
            (id) =>
              items.find((item) => item[key] === id)?.name ||
              "Sin nombre disponible",
          )
          .join(" · ")
      : "—";
  const active = read.rows.filter((row) => row.status === "PUBLISHED");
  const priced = active
    .map(productPriceWithTax)
    .filter((value) => value !== null);
  const average = priced.length
    ? priced.reduce((sum, value) => sum + value, 0) / priced.length
    : null;
  const filtersActive = Boolean(
    view.search || view.status || view.category || view.catalog,
  );
  const emptyReview = !filtersActive && view.tab === "review";
  const emptyDraft = !filtersActive && view.tab === "draft";
  const drafts = read.rows.filter((row) => row.status === "DRAFT").length;
  const issues = read.rows.filter((row) => row.missingFields.length > 0).length;
  const exportRows = () =>
    exportCollection(
      rows,
      [
        { label: "Referencia", value: (row) => row.reference },
        { label: "Producto", value: (row) => row.name },
        {
          label: "Categorías",
          value: (row) => relation(row.categoryIds, categories, "categoryId"),
        },
        {
          label: "Catálogos",
          value: (row) => relation(row.catalogIds, catalogs, "catalogId"),
        },
        { label: "Precio con IVA", value: (row) => productPriceWithTax(row) },
        { label: "Estado", value: (row) => row.status },
        {
          label: "Datos por revisar",
          value: (row) => row.missingFields.join(", "),
        },
      ],
      "productos",
    );
  const requestDelete = (products, returnId) => {
    setMenu(null);
    setDeletion({ products, returnId });
  };
  const closeDelete = () => {
    const id = deletion?.returnId;
    setDeletion(null);
    requestAnimationFrame(() => document.getElementById(id)?.focus());
  };
  return (
    <section className="productsList">
      <div className="productCatalog" inert={deletion ? "" : undefined}>
        <header className="productCatalogHeader">
          <div>
            <h1>Productos</h1>
            <p>Tu catálogo, sus precios y los datos de cada ficha.</p>
          </div>
          <div className="productCatalogActions">
            <button
              type="button"
              disabled={
                read.pending || mutationBusy || read.error || !rows.length
              }
              onClick={exportRows}
            >
              <LuDownload aria-hidden="true" />
              Exportar
            </button>
            <button
              type="button"
              className="primary"
              disabled={mutationBusy}
              onClick={() => open()}
            >
              <LuPlus aria-hidden="true" />
              Crear producto
            </button>
          </div>
        </header>
        {!read.error && (
          <div
            className="productCatalogStats"
            aria-label="Resumen del catálogo"
          >
            <div>
              <span className="productStatIcon">
                <LuBox aria-hidden="true" />
              </span>
              <div>
                <span>Productos activos</span>
                <strong>{read.pending ? "…" : active.length}</strong>
                <small>
                  {read.pending
                    ? "Cargando catálogo"
                    : `Precio medio ${productMoney(average)} · IVA incl.`}
                </small>
              </div>
            </div>
            <div>
              <span className="productStatIcon amber">
                <LuFileText aria-hidden="true" />
              </span>
              <div>
                <span>En borrador</span>
                <strong>{read.pending ? "…" : drafts}</strong>
                <small>Pendientes de publicación</small>
              </div>
            </div>
            <div>
              <span className="productStatIcon rose">
                <LuTriangleAlert aria-hidden="true" />
              </span>
              <div>
                <span>Con incidencias</span>
                <strong>
                  {read.pending ? "…" : read.reviewError ? "—" : issues}
                </strong>
                <small>
                  {read.reviewError
                    ? "Revisión no disponible"
                    : "Nombre, precio o lote obligatorio"}
                </small>
              </div>
            </div>
          </div>
        )}
        <div className="productCatalogFrame" aria-busy={read.pending}>
          <nav className="productCatalogTabs" aria-label="Vistas de productos">
            {[
              ["all", "Productos", read.rows.length],
              ["draft", "En borrador", drafts],
              ["review", "Monitoreo", read.reviewError ? "—" : issues],
            ].map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                aria-pressed={view.tab === key}
                disabled={
                  read.pending ||
                  read.error ||
                  (key === "review" && read.reviewError)
                }
                onClick={() => changeView("tab", key)}
              >
                {label}
                <span>{read.pending ? "…" : read.error ? "—" : count}</span>
              </button>
            ))}
          </nav>
          {read.pending && (
            <p className="productCatalogFeedback" role="status">
              Cargando productos…
            </p>
          )}
          {read.error ? (
            <div className="productCatalogEmpty" role="alert">
              <LuTriangleAlert aria-hidden="true" />
              <h2>No hemos podido cargar los productos</h2>
              <p>Vuelve a intentarlo para consultar tu catálogo.</p>
              <button
                type="button"
                onClick={() => setRetry((value) => value + 1)}
              >
                <LuRefreshCw aria-hidden="true" />
                Reintentar
              </button>
            </div>
          ) : (
            <>
              {read.reviewError && (
                <div className="productReviewError" role="alert">
                  El catálogo está disponible, pero no hemos podido cargar la
                  revisión de fichas.
                  <button
                    type="button"
                    disabled={read.pending || mutationBusy}
                    onClick={() => setRetry((value) => value + 1)}
                  >
                    Reintentar revisión
                  </button>
                </div>
              )}
              <div className="productCatalogTools">
                <div className="productCatalogSearchRow">
                  <label className="productCatalogSearch">
                    <LuSearch aria-hidden="true" />
                    <input
                      type="search"
                      aria-label="Buscar productos"
                      placeholder="Buscar por referencia o nombre"
                      value={view.search}
                      onChange={(e) => changeView("search", e.target.value)}
                    />
                  </label>
                  <ProductSort value={view.sort} onChange={value => changeView("sort", value)} />
                  <span className="productCatalogCount" role="status">
                    {rows.length} {rows.length === 1 ? "producto" : "productos"}
                  </span>
                </div>
                <div className="productCatalogFilters">
                  <label>
                    <span className="visually-hidden">Estado</span>
                    <select
                      aria-label="Estado"
                      value={view.status}
                      onChange={(e) => changeView("status", e.target.value)}
                    >
                      <option value="">Estado</option>
                      <option value="PUBLISHED">Activados</option>
                      <option value="DRAFT">Borradores</option>
                    </select>
                  </label>
                  <label>
                    <span className="visually-hidden">Catálogo</span>
                    <select
                      aria-label="Catálogo"
                      value={view.catalog}
                      onChange={(e) => changeView("catalog", e.target.value)}
                    >
                      <option value="">Catálogo</option>
                      {catalogs.map((item) => (
                        <option key={item.catalogId} value={item.catalogId}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span className="visually-hidden">Categoría</span>
                    <select
                      aria-label="Categoría"
                      value={view.category}
                      onChange={(e) => changeView("category", e.target.value)}
                    >
                      <option value="">Categoría</option>
                      {categories.map((item) => (
                        <option key={item.categoryId} value={item.categoryId}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  {(view.search ||
                    view.status ||
                    view.category ||
                    view.catalog) && (
                    <button
                      type="button"
                      className="productCatalogClear"
                      onClick={clear}
                    >
                      <LuX aria-hidden="true" />
                      Limpiar filtros
                    </button>
                  )}
                </div>
              </div>
              {selected.length > 0 && (
                <div className="productCatalogSelection">
                  <strong>
                    {selected.length}{" "}
                    {selected.length === 1 ? "seleccionado" : "seleccionados"}
                  </strong>
                  <button
                    type="button"
                    disabled={read.pending || mutationBusy}
                    onClick={() => setSelected([])}
                  >
                    Cancelar selección
                  </button>
                  <button
                    type="button"
                    id="product-bulk-delete"
                    disabled={read.pending || mutationBusy}
                    onClick={() =>
                      requestDelete(
                        read.rows.filter((row) =>
                          selected.includes(row.productId),
                        ),
                        "product-bulk-delete",
                      )
                    }
                  >
                    <LuTrash2 aria-hidden="true" />
                    Eliminar seleccionados
                  </button>
                </div>
              )}
              {!read.pending && !rows.length ? (
                <div className="productCatalogEmpty">
                  <LuBox aria-hidden="true" />
                  <h2>
                    {read.rows.length
                      ? emptyReview
                        ? "No hay datos pendientes de revisión"
                        : emptyDraft
                          ? "No tienes productos en borrador"
                          : "No hay productos con estos filtros"
                      : "Todavía no tienes productos"}
                  </h2>
                  <p>
                    {read.rows.length
                      ? emptyReview
                        ? "Las fichas tienen completos los datos obligatorios que comprueba esta revisión."
                        : emptyDraft
                          ? "Los productos de este catálogo están publicados."
                          : "Cambia la búsqueda o restablece la vista para ver tu catálogo."
                      : "Crea tu primer producto para empezar a preparar la carta."}
                  </p>
                  <button
                    type="button"
                    onClick={read.rows.length ? clear : () => open()}
                  >
                    {read.rows.length ? "Restablecer vista" : "Crear producto"}
                  </button>
                </div>
              ) : (
                <div className="productCatalogTableWrap">
                  <table className="productCatalogTable">
                    <thead>
                      <tr>
                        <th className="productSelectionCell">
                          <input
                            ref={allBox}
                            type="checkbox"
                            aria-label="Seleccionar productos de esta página"
                            disabled={read.pending || !pageRows.length}
                            checked={allSelected}
                            onChange={() =>
                              setSelected((previous) =>
                                allSelected
                                  ? previous.filter(
                                      (id) =>
                                        !pageRows.some(
                                          (row) => row.productId === id,
                                        ),
                                    )
                                  : [
                                      ...new Set([
                                        ...previous,
                                        ...pageRows.map((row) => row.productId),
                                      ]),
                                    ],
                              )
                            }
                          />
                        </th>
                        <th>Imagen</th>
                        <th>Referencia</th>
                        <th>Producto</th>
                        <th>Categoría</th>
                        <th>Catálogo</th>
                        <th>
                          Precio <small>IVA incl.</small>
                        </th>
                        <th>Estado</th>
                        <th>Incidencias</th>
                        <th>
                          <span className="visually-hidden">Acciones</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((row) => (
                        <tr
                          key={row.productId}
                          className={
                            selected.includes(row.productId)
                              ? "is-selected"
                              : ""
                          }
                        >
                          <td className="productSelectionCell">
                            <input
                              type="checkbox"
                              aria-label={`Seleccionar ${row.name || "producto sin nombre"}`}
                              disabled={read.pending || mutationBusy}
                              checked={selected.includes(row.productId)}
                              onChange={() => toggle(row.productId)}
                            />
                          </td>
                          <td className="productCatalogImageCell">
                            <span className="productListImage">
                              {row.images?.[0] ? (
                                <img
                                  src={row.images[0]}
                                  alt=""
                                  loading="lazy"
                                  onError={(e) => {
                                    e.currentTarget.hidden = true;
                                  }}
                                />
                              ) : (
                                <LuImage aria-hidden="true" />
                              )}
                            </span>
                          </td>
                          <td
                            className="productCatalogReference"
                            title={row.reference || "Sin referencia"}
                            data-label="Referencia"
                          >
                            {row.reference || "—"}
                          </td>
                          <td className="productCatalogName">
                            <button
                              type="button"
                              disabled={read.pending || mutationBusy}
                              aria-label={`Editar ${row.name || "producto sin nombre"}`}
                              title={row.name || "Producto sin nombre"}
                              onClick={() => open(row)}
                            >
                              {row.name || "Producto sin nombre"}
                            </button>
                          </td>
                          <td data-label="Categoría">
                            {relation(
                              row.categoryIds,
                              categories,
                              "categoryId",
                            )}
                          </td>
                          <td data-label="Catálogo">
                            {relation(row.catalogIds, catalogs, "catalogId")}
                          </td>
                          <td
                            className="productCatalogPrice"
                            data-label="Precio · IVA incl."
                          >
                            {productMoney(productPriceWithTax(row))}
                          </td>
                          <td className="productCatalogStatusCell">
                            <span
                              className={`productCatalogStatus ${row.status === "PUBLISHED" ? "published" : "draft"}`}
                            >
                              {row.status === "PUBLISHED"
                                ? "Activado"
                                : "Borrador"}
                            </span>
                          </td>
                          <td
                            className="productCatalogReview"
                            data-label="Datos por revisar"
                          >
                            {read.reviewError ? (
                              "No disponible"
                            ) : row.missingFields.length ? (
                              <ProductMonitoringFields
                                fields={row.missingFields}
                              />
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="productCatalogActionsCell">
                            <div className="productRowActions">
                              <button
                                type="button"
                                id={`product-actions-${row.productId}`}
                                aria-label={`Acciones de ${row.name || "producto sin nombre"}`}
                                className="productRowTrigger"
                                aria-expanded={menu?.id === row.productId}
                                disabled={read.pending || mutationBusy}
                                onClick={(event) => {
                                  const rect =
                                    event.currentTarget.getBoundingClientRect();
                                  setMenu((previous) =>
                                    previous?.id === row.productId
                                      ? null
                                      : {
                                          id: row.productId,
                                          top: Math.max(
                                            12,
                                            Math.min(
                                              rect.bottom + 6,
                                              window.innerHeight - 230,
                                            ),
                                          ),
                                          left: Math.max(
                                            12,
                                            Math.min(
                                              rect.right - 200,
                                              window.innerWidth - 212,
                                            ),
                                          ),
                                        },
                                  );
                                }}
                              >
                                <LuEllipsis aria-hidden="true" />
                              </button>
                              {menu?.id === row.productId &&
                                createPortal(
                                  <div
                                    ref={menuRef}
                                    style={{ top: menu.top, left: menu.left }}
                                    className="productRowMenu"
                                    role="group"
                                    aria-label={`Opciones de ${row.name}`}
                                  >
                                    <button
                                      type="button"
                                      disabled={mutationBusy}
                                      onClick={() => open(row)}
                                    >
                                      Editar
                                    </button>
                                    <ProductStatusToggle
                                      presentation="menu"
                                      onBusyChange={setMutationBusy}
                                      productId={row.productId}
                                      name={row.name}
                                      status={row.status}
                                      onConfirmed={() => setMenu(null)}
                                    />
                                    <button
                                      type="button"
                                      className="danger"
                                      disabled={mutationBusy}
                                      onClick={() =>
                                        requestDelete(
                                          [row],
                                          `product-actions-${row.productId}`,
                                        )
                                      }
                                    >
                                      Eliminar
                                    </button>
                                  </div>,
                                  document.body,
                                )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!read.pending && (
                <footer className="productCatalogFooter">
                  <span>
                    {rows.length} {rows.length === 1 ? "producto" : "productos"}
                    {selected.length
                      ? ` · ${selected.length} ${selected.length === 1 ? "seleccionado" : "seleccionados"}`
                      : ""}
                  </span>
                  {pages > 1 && (
                    <nav aria-label="Paginación de productos">
                      <button
                        type="button"
                        disabled={currentPage === 1}
                        onClick={() => {
                          setPage(currentPage - 1);
                          setMenu(null);
                        }}
                      >
                        Anterior
                      </button>
                      <span>
                        {currentPage} / {pages}
                      </span>
                      <button
                        type="button"
                        disabled={currentPage === pages}
                        onClick={() => {
                          setPage(currentPage + 1);
                          setMenu(null);
                        }}
                      >
                        Siguiente
                      </button>
                    </nav>
                  )}
                </footer>
              )}
            </>
          )}
        </div>
      </div>
      <Edition pageEditor />
      {deletion && (
        <DeleteProducts
          products={deletion.products}
          onClose={closeDelete}
          onReview={() => {
            closeDelete();
            dispatch(requestTableRefresh());
          }}
          onDeleted={() => {
            closeDelete();
            setSelected([]);
            dispatch(requestTableRefresh());
          }}
        />
      )}
    </section>
  );
};

const DeleteProducts = ({ products, onClose, onDeleted, onReview }) => {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const guard = useRef(false),
    dialog = useRef(null);
  useEffect(() => {
    dialog.current?.querySelector("button")?.focus();
  }, []);
  useEffect(() => {
    const element = dialog.current;
    const keys = (event) => {
      if (event.key === "Escape" && !busy) {
        event.preventDefault();
        onClose();
      }
      if (event.key !== "Tab") return;
      const buttons = Array.from(
        element.querySelectorAll("button:not(:disabled)"),
      );
      if (!buttons.length) {
        event.preventDefault();
        return;
      }
      if (event.shiftKey && document.activeElement === buttons[0]) {
        event.preventDefault();
        buttons[buttons.length - 1].focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === buttons[buttons.length - 1]
      ) {
        event.preventDefault();
        buttons[0].focus();
      }
    };
    element.addEventListener("keydown", keys);
    return () => element.removeEventListener("keydown", keys);
  }, [busy, onClose]);
  const remove = async () => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError("");
    try {
      const response = await deleteProductsBatch(
        products.map((row) => row.productId),
      );
      if (response?.error !== false) throw new Error("rejected");
      onDeleted();
    } catch {
      setError(
        "No hemos podido confirmar la eliminación. Revisa el listado antes de volver a intentarlo.",
      );
    } finally {
      guard.current = false;
      setBusy(false);
    }
  };
  return (
    <div className="productDeleteBackdrop">
      <div
        ref={dialog}
        className="productDeleteDialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="product-delete-title"
        aria-describedby="product-delete-description"
        aria-busy={busy}
      >
        <h2 id="product-delete-title">
          {products.length === 1
            ? "¿Eliminar este producto?"
            : `¿Eliminar ${products.length} productos?`}
        </h2>
        <p id="product-delete-description">
          Se eliminarán del catálogo los productos seleccionados.
        </p>
        <ul>
          {products.slice(0, 3).map((row) => (
            <li key={row.productId}>{row.name || "Producto sin nombre"}</li>
          ))}
        </ul>
        {products.length > 3 && <p>Y {products.length - 3} productos más.</p>}
        {error && (
          <div>
            <p role="alert">{error}</p>
            <button type="button" disabled={busy} onClick={onReview}>
              Revisar listado
            </button>
          </div>
        )}
        <div>
          <button type="button" disabled={busy} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="danger"
            disabled={busy}
            onClick={remove}
          >
            {busy ? "Eliminando…" : "Eliminar"}
          </button>
        </div>
      </div>
    </div>
  );
};
